import { FormControl, MenuItem, Select } from "@mui/material";
import AuthContext from "context/AuthContext";
import dayjs from "dayjs";
import useToastContext from "hooks/useToastContext";
import { isEmpty } from "lodash";
import Fab from "pages/test_manager/components/Fab";
import QuizAgentsModal from "pages/test_manager/components/QuizAgentsModal";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaExternalLinkAlt } from "react-icons/fa";
import { TiEye, TiPencil, TiPlus, TiTrash, TiUser } from "react-icons/ti";
import { Link, useNavigate } from "react-router-dom";
import { apiDelete, apiGet } from "utils/apiFetch";
import apiPath from "utils/apiPath";
import { QUIZ_TEST_CATEGORIES } from "utils/constants";
import helpers from "utils/helpers";

function TestsPage() {
  const { t } = useTranslation();
  const [tests, setTests] = useState([]);
  const [showAgentsModal, setShowAgentsModal] = useState(false);
  const [selectedQuizId, setSelectedQuizId] = useState("");
  const [agentsQuizTitle, setAgentsQuizTitle] = useState("");
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsAttempted, setAgentsAttempted] = useState([]);
  const [agentsNotAttempted, setAgentsNotAttempted] = useState([]);
  const [agentsPassOutcome, setAgentsPassOutcome] = useState("");
  const [testCategory, setTestCategory] = useState("");
  const categories = useMemo(() => QUIZ_TEST_CATEGORIES, []);
  const navigate = useNavigate();
  const notification = useToastContext();
  const fetchTests = useCallback(
    async function () {
      try {
        const payload = {};
        const path = apiPath.quizTests;
        if (testCategory) {
          payload.testCategory = testCategory;
        }
        const result = await apiGet(path, payload);
        setTests(result?.data?.results?.docs);
      } catch (error) {
        console.log(error);
        notification.error(error?.response?.data?.message);
      }
    },
    [notification, testCategory],
  );
  const { user } = useContext(AuthContext);
  const permissions = user?.permission?.find(
    (e) => e.manager === "test_manager",
  );
  useEffect(() => {
    fetchTests();
  }, [fetchTests, testCategory]);

  useEffect(() => {
    if (!showAgentsModal || !selectedQuizId) return;
    let cancelled = false;
    const loadAgents = async () => {
      setAgentsLoading(true);
      setAgentsAttempted([]);
      setAgentsNotAttempted([]);
      try {
        const path = `${apiPath.quizTests}/${selectedQuizId}/agents`;
        const result = await apiGet(path, {
          ...(agentsPassOutcome && { passOutcome: agentsPassOutcome }),
        });
        if (cancelled) return;
        const data = result?.data?.results;
        setAgentsAttempted(data?.attemptedAgents ?? []);
        setAgentsNotAttempted(data?.notAttemptedAgents ?? []);
      } catch (error) {
        if (!cancelled) {
          notification.error(error?.response?.data?.message);
        }
      } finally {
        if (!cancelled) setAgentsLoading(false);
      }
    };
    loadAgents();
    return () => {
      cancelled = true;
    };
  }, [showAgentsModal, selectedQuizId, agentsPassOutcome, notification]);

  function closeAgentsModal() {
    setShowAgentsModal(false);
    setSelectedQuizId("");
    setAgentsAttempted([]);
    setAgentsNotAttempted([]);
    setAgentsQuizTitle("");
    setAgentsPassOutcome("");
  }

  function handleShowAgentsModal(quizId, title) {
    setSelectedQuizId(quizId);
    setAgentsQuizTitle(title || "");
    setShowAgentsModal(true);
  }

  async function deleteTestHandler(id) {
    try {
      const path = apiPath.quizTests + "/" + id;
      const result = await apiDelete(path);
      if (result?.status === 200) {
        notification.success(t("O_TEST_DELETED_SUCCESSFULLY"));
        fetchTests();
      }
    } catch (error) {
      console.log(error);
      notification.error(error?.response?.data?.message);
    }
  }
  return (
    <div className="bg-white px-2 py-4 flex flex-col gap-4">
      <h1 className="text-2xl font-bold">{t("O_TESTS")}</h1>

      {(permissions?.add || user?.role === "admin") && (
        <Fab onClick={() => navigate("/test-manager/add-test")}>
          <TiPlus /> Create Test
        </Fab>
      )}

      <div className="overflow-x-auto relative border">
        <div className="flex justify-between items-center gap-2 px-2 my-2">
          <FormControl className="w-[200px]">
            <Select
              value={testCategory}
              displayEmpty
              onChange={(e) => setTestCategory(e.target.value)}
            >
              <MenuItem value="">{t("O_ALL")}</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category._id} value={category._id}>
                  {category.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <table className="w-full text-xs text-left text-[#A5A5A5] dark:text-gray-400 ">
          <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="py-3 px-6">
                #
              </th>

              <th scope="col" className="py-3 px-6 cursor-pointer">
                {t("O_TITLE")}
              </th>

              <th scope="col" className="py-3 px-6 text-center">
                {t("O_DESCRIPTION")}
              </th>
              <th scope="col" className="py-3 px-6">
                {t("O_STATUS")}
              </th>
              <th scope="col" className="py-3 px-6">
                {t("O_START_TIME")}
              </th>
              <th scope="col" className="py-3 px-6">
                {t("O_END_TIME")}
              </th>
              <th scope="col" className="py-3 px-6 cursor-pointer">
                {t("O_QUESTIONS")}
              </th>

              <th scope="col" className="py-3 px-6 cursor-pointer">
                {t("O_TEST_TYPE")}
              </th>
              <th scope="col" className="py-3 px-6 cursor-pointer">
                {t("O_TEST_CATEGORY")}
              </th>
              <th scope="col" className="py-3 px-6 cursor-pointer">
                Attempts
              </th>
              <th scope="col" className="py-3 px-6 cursor-pointer">
                Submissions
              </th>

              <th scope="col" className="py-3 px-6 cursor-pointer">
                {t("O_ACTION")}
              </th>
            </tr>
          </thead>
          <tbody>
            {tests &&
              tests?.map((item, i) => (
                <tr className="border-b border-[#E1E6EE]" key={item._id}>
                  <td className="py-3 px-6 border-l">{i + 1}</td>
                  <td className="py-3 px-6 border-l">{item.title}</td>
                  <td className="py-3 px-6 border-l">{item.description}</td>
                  <td className="py-3 px-6 border-l">
                    {item.status === "active" ? (
                      <span className="text-green-500">{t("O_ACTIVE")}</span>
                    ) : (
                      <span className="text-red-500">{t("O_INACTIVE")}</span>
                    )}
                  </td>

                  <td className="py-3 px-6 border-l">
                    {dayjs(item.startTime).isValid()
                      ? dayjs(item.startTime).format("DD-MM-YYYY hh:mm A")
                      : "N/A"}
                  </td>
                  <td className="py-3 px-6 border-l">
                    {dayjs(item.endTime).isValid()
                      ? dayjs(item.endTime).format("DD-MM-YYYY hh:mm A")
                      : "N/A"}
                  </td>
                  <td className="py-3 px-6 border-l">
                    <Link
                      className="text-green-400 hover:text-green-700 font-bold"
                      to={`/test-manager/questions?testId=${item._id}`}
                    >
                      {item?.questionCount || 0}
                    </Link>
                  </td>
                  <td className="py-3 px-6 border-l">
                    {item.testType?.toUpperCase() || "N/A"}
                  </td>
                  <td className="py-3 px-6 border-l">
                    {item?.category?.toUpperCase() || "N/A"}
                  </td>
                  <td className="py-3 px-6 border-l">
                    {item.attempts || "N/A"}
                  </td>
                  <td className="py-3 px-6 border-l"></td>
                  <td className="py-3 px-6 border-l">
                    <div className="flex flex-col gap-2">
                      <div className="flex justify-center gap-2">
                        {(permissions?.view || user?.role === "admin") && (
                          <button
                            className="bg-green-500 hover:bg-green-700 transition-colors duration-150 text-white p-2 rounded-full"
                            onClick={() =>
                              navigate(
                                `/test-manager/tests/details/${item._id}`,
                              )
                            }
                          >
                            <TiEye />
                          </button>
                        )}
                        {(permissions?.view || user?.role === "admin") && (
                          <button
                            className="bg-blue-500 hover:bg-blue-700 transition-colors duration-150 text-white p-2 rounded-full"
                            onClick={() =>
                              handleShowAgentsModal(item._id, item.title)
                            }
                            title={t("O_AGENTS")}
                          >
                            <TiUser />
                          </button>
                        )}
                        {(permissions?.add || user?.role === "admin") && (
                          <button
                            className="bg-blue-500 hover:bg-blue-700 transition-colors duration-150 text-white p-2 rounded-full"
                            onClick={() =>
                              navigate(`/test-manager/tests/edit/${item._id}`)
                            }
                          >
                            <TiPencil />
                          </button>
                        )}
                        {(permissions?.delete || user?.role === "admin") && (
                          <button
                            className="bg-red-500 hover:bg-red-700 transition-colors duration-150 text-white p-2 rounded-full"
                            onClick={() =>
                              helpers.alertFunction(
                                `Are you sure want to delete ${item.title}?`,
                                item._id,
                                deleteTestHandler,
                                true,
                              )
                            }
                          >
                            <TiTrash />
                          </button>
                        )}
                      </div>
                      <Link
                        className="rounded-full bg-[#8FC150] hover:bg-[#7eae43] transition-all duration-300 text-white flex items-center gap-1 px-2 py-1"
                        to={`/test-manager/submissions?testId=${item._id}`}
                      >
                        <FaExternalLinkAlt /> <span>Submissions</span>
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            {isEmpty(tests) && (
              <tr className="bg-white border-b w-full text-center dark:bg-gray-800 dark:border-gray-700">
                <td className="py-4 px-6" colSpan={12}>
                  {t("O_NO_RECORD_FOUND")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <QuizAgentsModal
        open={showAgentsModal}
        onClose={closeAgentsModal}
        quizTitle={agentsQuizTitle}
        loading={agentsLoading}
        agentsAttempted={agentsAttempted}
        agentsNotAttempted={agentsNotAttempted}
        passOutcome={agentsPassOutcome}
        onPassOutcomeChange={setAgentsPassOutcome}
      />
    </div>
  );
}

export default TestsPage;
